<script setup lang="ts">
import { type UiAccordion } from '@afs/design-system'
import { useI18n } from 'vue-i18n'
import { navigate } from 'vike/client/router'
import { useFormatSeoContent } from '@/composables/useFormatSeoContent'
import { useGtmOnPageMounted, useGtmSimulatorClick } from '@/composables/useGtmTag'
import type { ParentHomeData } from '@/pages/(app)/index/+data'
import type { ProductHubPageData } from '@/pages/(app)/@productHub/+data'
import { useSimulation } from '@/composables/useSimulation'
import { onMounted, ref } from 'vue'
import { openLegalNotice, scrollToLegalNotice } from '@/composables/useAccordion'

const props = withDefaults(defineProps<Props>(), {
  data: () => <ParentHomeData | ProductHubPageData>{},
})

type Props = {
  data: ParentHomeData | ProductHubPageData
  displayBreadcrumb: boolean
}

if (props.data?.productHub) {
  useFormatSeoContent(props.data.productHub.seoTitle, props.data.productHub.hero.subtitle)
  useGtmOnPageMounted('other')
}

const { t } = useI18n()
const trackSubmit = useGtmSimulatorClick()

const legalNoticeAccordion = ref<InstanceType<typeof UiAccordion> | null>(null)
onMounted(() => openLegalNotice(legalNoticeAccordion.value))

const { heroProps, additionnalLegalProps } = useSimulation(props.data.productHub)

const breadcrumb = {
  ariaLabel: t('breadcrumb.ariaLabel'),
  items: [
    { text: t('page.accueil'), to: '/' },
    {
      text:
        props.data.productHub?.breadcrumbTitle && props.data.productHub.breadcrumbTitle.length > 40
          ? `${props.data.productHub.breadcrumbTitle.slice(0, 40)}...`
          : props.data.productHub?.breadcrumbTitle,
    },
  ],
}

const onSimulatorSubmit = (payload: string | number | null, event?: Event) => {
  if (typeof payload === 'string') return

  if (event && event.type === 'submit') {
    const submitEvent = event as SubmitEvent

    if (submitEvent.submitter?.innerText) {
      trackSubmit(submitEvent.submitter.innerText)
    }
  }
  const searchParams = new URLSearchParams()
  if (payload) searchParams.set('amount', payload?.toString())
  navigate(`/simulateur?${searchParams}`)
}

const productHubSimulatordProps = props.data.productHub?.hero.simulator
  ? {
      ...props.data.productHub.hero.simulator,
      action: onSimulatorSubmit,
    }
  : undefined

const legalNotice = {
  id: 'product-hub-legal-notice',
  title: t('homepage.mentionLegal.title'),
}
</script>

<template>
  <div
    v-if="props.data.productHub"
    class="OfferHub"
  >
    <molecules-ui-breadcrumb
      v-if="displayBreadcrumb"
      v-bind="breadcrumb"
      class="OfferHub__breadcrumb"
    />
    <organisms-ui-hero
      v-if="heroProps"
      class="Hero"
      v-bind="heroProps"
      :simulator="productHubSimulatordProps"
      :legal-notice-button-label="$t('product.hero.legalNoticeButton')"
      @hero::on-legal-notice-click="scrollToLegalNotice(legalNoticeAccordion)"
    />
    <div class="container my-8">
      <section class="grid">
        <div class="OfferHub__text color-blue-01">
          <h2 class="heading-2">{{ props.data.productHub.blockText.title }}</h2>
          <h3 class="paragraph-2 mb-4 color-red-01">
            {{ props.data.productHub.blockText.subtitle }}
          </h3>
          <p class="paragraph-2 mb-2 longtext">
            {{ props.data.productHub.blockText.chapo }}
          </p>
          <molecules-ui-rich-text
            v-if="props.data.productHub.blockText.content"
            :content="props.data.productHub.blockText.content"
          />
        </div>
      </section>
    </div>
    <organisms-ui-offers
      v-if="props.data.productHub?.blockOffre"
      :title="props.data.productHub.blockOffre.title"
      :sub-title="props.data.productHub.blockOffre.subTitle"
      :offers="props.data.productHub.blockOffre.offers"
    />
    <div
      v-if="props.data.productHub.blockAdvantages"
      class="container my-8"
    >
      <section class="OfferHub__advantages grid">
        <div class="OfferHub__advantages__heading color-blue-01">
          <h2 class="heading-2 mb-2">{{ props.data.productHub.blockAdvantages.title }}</h2>
          <p class="paragraph-3 longtext">{{ props.data.productHub.blockAdvantages.description }}</p>
        </div>
        <ul class="OfferHub__advantages__list g-4">
          <template
            v-for="(advantage, index) in props.data.productHub.blockAdvantages.items"
            :key="`offerHub-advantage-${index}`"
          >
            <li
              v-if="advantage.text"
              class="OfferHub__advantages__item"
            >
              <molecules-ui-list-item :text="advantage.text" />
            </li>
          </template>
        </ul>
      </section>
    </div>
    <organisms-ui-loan-ranges
      v-if="props.data.productHub.blockAllOffers"
      v-bind="props.data.productHub.blockAllOffers"
    />
    <div
      v-if="props.data.productHub.blockArgument"
      class="container my-8"
    >
      <section class="grid">
        <molecules-ui-highlight
          class="OfferHub__highlight"
          v-bind="props.data.productHub.blockArgument"
        />
      </section>
    </div>
    <div
      v-if="props.data.productHub.contactBlock"
      class="container my-8"
    >
      <section class="grid">
        <molecules-ui-rich-text
          v-if="props.data.productHub.contactBlock.text"
          class="Producthub__contact__text"
          :content="props.data.productHub.contactBlock.text"
        />
        <molecules-ui-contact
          v-if="props.data.productHub.contactBlock.contact"
          class="Producthub__contact__phone"
          v-bind="props.data.productHub.contactBlock.contact"
        />
      </section>
    </div>
    <div class="container my-8">
      <section class="grid">
        <molecules-ui-accordion
          ref="legalNoticeAccordion"
          class="ProductHub__legalNotice mt-4"
          :items="[legalNotice]"
        >
          <template
            v-for="item in [legalNotice]"
            :key="item.id"
            #[item.id]
          >
            <molecules-ui-rich-text
              v-if="additionnalLegalProps"
              class="mt-3"
              :content="additionnalLegalProps"
            />
            <molecules-ui-rich-text
              v-if="props.data.productHub.legalNoticeBlock.legalNotice"
              class="mt-3"
              :content="props.data.productHub.legalNoticeBlock.legalNotice"
            />
          </template>
        </molecules-ui-accordion>
      </section>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use 'sass:map';
@use 'config/spaces';
@use 'utilities/mq';

.OfferHub__breadcrumb {
  position: absolute;
  top: 0;
}

.OfferHub__text,
.OfferHub__highlight,
.OfferHub__advantages__heading,
.OfferHub__advantages__list {
  grid-column: span 4;

  @include mq.media('>=medium') {
    grid-column: 2 / span 10;
  }
}

.OfferHub__advantages__heading {
  margin-block-end: map.get(spaces.$sizes, '5');

  @include mq.media('>=small') {
    margin-block-end: map.get(spaces.$sizes, '6');
  }
}

.OfferHub__advantages__list {
  display: grid;

  @include mq.media('>=small') {
    grid-template-columns: repeat(3, 1fr);
  }
}

.Producthub__contact__text {
  grid-column: 2 / span 5;
}

.Producthub__contact__phone {
  grid-column: span 5;
}

.ProductHub__legalNotice {
  grid-column: span 4;

  @include mq.media('>=medium') {
    grid-column: 2 / span 10;
  }
}

.ProductHub__argument__element {
  grid-column: 2 / span 4;
  height: fit-content;

  @include mq.media('>=medium') {
    &:first-child {
      grid-column: span 6;
    }

    &:last-child {
      grid-column: span 6;
    }

    &:only-child {
      grid-column: 2 / span 10;
    }
  }

  @include mq.media('<=medium') {
    &:first-child {
      margin-bottom: 30px;
    }
  }
}
</style>
